import { useState } from "react";
import { toast } from "sonner";
import MaskedReveal from "@/components/MaskedReveal";
import FadeUp from "@/components/FadeUp";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatError, WHATSAPP_LINK } from "@/lib/api";
import { ArrowUpRight, Phone, Mail, MapPin } from "lucide-react";
import NAPBlock from "@/components/NAPBlock";
import { PORTRAITS, PORTRAIT_ALT } from "@/data/portraits";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import { breadcrumbListJsonLd, medicalWebPageJsonLd, localBusinessJsonLd } from "@/data/schema";

const COUNTRIES = ["India","Canada","United States","United Kingdom","Australia","United Arab Emirates","Saudi Arabia","Other"];
const CONDITIONS = ["Aortic Aneurysm","Peripheral Artery Disease","Deep Vein Thrombosis","Varicose Veins","Dialysis Access","Uterine Fibroids","Interventional Oncology","Not sure — need guidance","Other"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "India", condition: "", message: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please confirm consent to be contacted.");
    setSubmitting(true);
    try {
      const source = form.country && form.country !== "India" ? "nri" : "contact";
      const endpoint = source === "nri" ? "/nri-enquiry" : "/contact";
      await api.post(endpoint, { ...form, source });
      setDone(true);
      toast.success("Enquiry received. The team will reach out shortly.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-28 md:pt-36 pb-28">
      <SEO
        title="Contact Dr Tegbir Sidhu — WhatsApp, email or consultation form | Mohali"
        description="Speak to the doctor, not the desk. WhatsApp +91 96460 00444, email tegbirsidhu@gmail.com, or send an enquiry. Response within one working hour. Max Super Speciality Hospital, Mohali."
        keywords={["Dr Tegbir Sidhu contact", "WhatsApp Interventional Radiologist Mohali", "book vascular consultation Chandigarh", "Max Mohali IR appointment"]}
        url="/contact"
      />
      <JsonLd data={[
        localBusinessJsonLd(),
        medicalWebPageJsonLd({ name: "Contact Dr Tegbir Sidhu", description: "Direct WhatsApp, email and consultation form for Dr Tegbir Singh Sidhu at Max Super Speciality Hospital, Mohali.", url: "/contact" }),
        breadcrumbListJsonLd([{ label: "Home", to: "/" }, { label: "Contact", to: "/contact" }]),
      ]}/>
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          <FadeUp className="col-span-12 md:col-span-8">
            <div className="kbd">— Contact</div>
            <h1 className="mt-8 font-serif text-6xl md:text-[8vw] leading-[0.96] tracking-tightest">
              <MaskedReveal text={"Speak to the"}/>
              <MaskedReveal text={"doctor,"} delay={0.1} className="italic font-normal"/>
              <MaskedReveal text={"not the desk."} delay={0.2}/>
            </h1>
          </FadeUp>
          <FadeUp className="col-span-12 md:col-span-4" delay={0.2}>
            <div className="aspect-[3/4] overflow-hidden hairline noise-overlay relative">
              <img src={PORTRAITS.COAT_EDITORIAL} alt={PORTRAIT_ALT} className="w-full h-full object-cover" loading="lazy"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 text-bone">
                <div className="kbd text-white/80">Principal Consultant</div>
                <div className="font-serif text-lg mt-1 leading-tight">Dr Tegbir Singh Sidhu</div>
              </div>
            </div>
          </FadeUp>
        </div>

        <div className="grid grid-cols-12 gap-8 md:gap-16 mt-20">
          {/* Left — Details */}
          <FadeUp className="col-span-12 md:col-span-4">
            <div className="hairline-t pt-8">
              <div className="kbd">Reach directly</div>
              <a data-testid="contact-whatsapp" target="_blank" rel="noreferrer" href={WHATSAPP_LINK("Hello Dr Sidhu's team, I'd like to enquire about a consultation.")} className="mt-4 flex items-start gap-3 group">
                <Phone size={16} className="mt-1"/>
                <div>
                  <div className="font-serif text-2xl tracking-tightest group-hover:underline">+91 96460 00444</div>
                  <div className="kbd">WhatsApp Business</div>
                </div>
              </a>
              <a href="mailto:tegbirsidhu@gmail.com" data-testid="contact-email" className="mt-6 flex items-start gap-3">
                <Mail size={16} className="mt-1"/>
                <div>
                  <div className="font-serif text-2xl tracking-tightest">tegbirsidhu@gmail.com</div>
                  <div className="kbd">Direct inbox</div>
                </div>
              </a>
              <div className="mt-6 flex items-start gap-3">
                <MapPin size={16} className="mt-1"/>
                <div>
                  <div className="text-ink">Max Super Speciality Hospital<br/>Mohali, Punjab, India</div>
                  <div className="kbd mt-1">Vascular & Endovascular IR Suite</div>
                </div>
              </div>
              <div className="mt-10 kbd">Hours</div>
              <p className="mt-2 text-ink-muted leading-relaxed">Mon–Sat · 09:00 – 18:00 IST<br/>Video consults offered outside standard hours for NRI patients.</p>
            </div>
          </FadeUp>

          {/* Right — Form */}
          <FadeUp className="col-span-12 md:col-span-8" delay={0.15}>
            <div className="hairline p-6 md:p-10 bg-white/60 backdrop-blur">
              {done ? (
                <div data-testid="contact-success" className="py-16 text-center">
                  <div className="kbd">— Received</div>
                  <h3 className="mt-6 font-serif text-4xl md:text-6xl tracking-tightest">Thank you.</h3>
                  <p className="mt-6 text-ink-muted max-w-md mx-auto">
                    Your enquiry is with the team. You will hear from us within one working hour — often much sooner. A confirmation email is on its way to your inbox.
                  </p>
                  <a href={WHATSAPP_LINK("Hello, I just submitted an enquiry.")} target="_blank" rel="noreferrer" className="btn-pill-inv mt-10 inline-flex">Continue on WhatsApp <ArrowUpRight size={14}/></a>
                </div>
              ) : (
                <form data-testid="contact-form" onSubmit={submit} className="grid grid-cols-2 gap-6">
                  <div className="col-span-2 md:col-span-1">
                    <Label htmlFor="name" className="kbd">Name</Label>
                    <Input id="name" data-testid="input-name" required value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0" />
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <Label htmlFor="email" className="kbd">Email</Label>
                    <Input id="email" data-testid="input-email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0"/>
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <Label htmlFor="phone" className="kbd">Phone / WhatsApp</Label>
                    <Input id="phone" data-testid="input-phone" required value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0" placeholder="+1 · +44 · +91 …"/>
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <Label className="kbd">Country</Label>
                    <Select value={form.country} onValueChange={(v) => update("country", v)}>
                      <SelectTrigger data-testid="input-country" className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus:ring-0 text-lg px-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="kbd">Concern / condition</Label>
                    <Select value={form.condition} onValueChange={(v) => update("condition", v)}>
                      <SelectTrigger data-testid="input-condition" className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus:ring-0 text-lg px-0">
                        <SelectValue placeholder="Choose one — or 'Not sure'" />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="message" className="kbd">Tell us more (optional)</Label>
                    <Textarea id="message" data-testid="input-message" rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} className="mt-2 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0 resize-none"/>
                  </div>
                  <div className="col-span-2 flex items-start gap-3 mt-2">
                    <Checkbox id="consent" data-testid="input-consent" checked={form.consent} onCheckedChange={(v) => update("consent", Boolean(v))} className="mt-1"/>
                    <Label htmlFor="consent" className="text-sm text-ink-muted leading-relaxed">
                      I consent to being contacted by Dr Sidhu's team by phone, WhatsApp, or email, in accordance with the privacy policy.
                    </Label>
                  </div>
                  <div className="col-span-2 flex flex-wrap items-center justify-between gap-4 mt-4">
                    <div className="kbd">Response within one working hour</div>
                    <button data-testid="contact-submit" type="submit" disabled={submitting} className="btn-pill-inv disabled:opacity-60">
                      {submitting ? "Sending…" : "Send enquiry"}
                      <ArrowUpRight size={14}/>
                    </button>
                  </div>
                </form>
              )}
            </div>
          </FadeUp>
        </div>

        <FadeUp className="mt-20">
          <NAPBlock/>
        </FadeUp>
      </div>
    </div>
  );
}
